/**
 * src/components/ConnectOnboardingForm.jsx — embedded Stripe Connect Express
 * onboarding form.
 *
 * Alternative to redirecting the browser to Stripe's hosted onboarding page
 * (the `url` from POST /api/studio/connect/onboard): this mounts Stripe's
 * own onboarding UI in-page via `@stripe/connect-js`, backed by an Account
 * Session client secret from POST /api/studio/connect/session. Only rendered
 * by Settings.jsx's Payouts panel when the studio row carries a
 * `stripePublishableKey` (i.e. canUseEmbeddedPayments() on the API side) —
 * the mock/no-publishable-key case falls back to the redirect flow instead.
 *
 * The account itself is created lazily, inside `fetchClientSecret`, the
 * first time Stripe's SDK calls it — not before this component mounts — so
 * no Stripe account is created just by an owner viewing the Payouts panel.
 *
 * Same PCI/identity posture as the rest of Connect: bank and identity fields
 * are Stripe's own iframe, never this app's DOM or Worker.
 */
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js';
import { useEffect, useState } from 'react';

import { api } from '../lib/api.js';
import { onModeChange, readThemeVars } from '../lib/theme.js';

/**
 * Builds Connect's `appearance` from the live dashboard tokens, so the form
 * matches whichever mode (light/dark) is currently active. Monochrome
 * primary (text colour, not accent) to match the dashboard's own buttons.
 *
 * @returns {object} A Connect.js `appearance` option.
 */
function readAppearance() {
  const vars = readThemeVars(['surface', 'text', 'text-muted', 'danger', 'font-sans']);
  return {
    labels: 'floating',
    variables: {
      colorPrimary: vars.text,
      colorBackground: vars.surface,
      colorText: vars.text,
      colorSecondaryText: vars['text-muted'],
      colorDanger: vars.danger,
      borderRadius: '16px',
      fontFamily: vars['font-sans'],
      fontSizeBase: '14px',
    },
  };
}

/**
 * @param {object} props
 * @param {string} props.publishableKey Stripe publishable key, from the API.
 * @param {() => void} props.onExit Called once the embedded form's steps are
 *   complete (status may still be pending Stripe's own verification).
 * @returns {JSX.Element}
 */
export default function ConnectOnboardingForm({ publishableKey, onExit }) {
  const [instance] = useState(() =>
    loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret: async () => {
        const res = await api.post('/api/studio/connect/session');
        return res.clientSecret;
      },
      appearance: readAppearance(),
    }),
  );

  // Connect renders in Stripe's cross-origin iframes, so it can't see the
  // page's CSS — push a freshly-resolved appearance whenever the mode flips,
  // either via the picker (data-mode on <html>) or, in 'system' mode, via
  // the OS colour scheme changing underneath us.
  useEffect(
    () => onModeChange(() => instance.update({ appearance: readAppearance() })),
    [instance],
  );

  return (
    <ConnectComponentsProvider connectInstance={instance}>
      <ConnectAccountOnboarding onExit={onExit} />
    </ConnectComponentsProvider>
  );
}
